'use client';

import { GlobeIcon } from '@/components/shared/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { switchAppLanguage } from '@/lib/translation/switch-app-language-client';
import { useLanguage, useRTLUtils } from '@/lib/translation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { MobileSidePanelTheme } from '@/components/layout/mobile-app-side-panel-theme';

type MobileLanguageQuickButtonProps = {
  theme: MobileSidePanelTheme;
  className?: string;
};

/** Mobile side panel footer: compact globe for language switch. */
export function MobileLanguageQuickButton({ theme, className }: MobileLanguageQuickButtonProps) {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const { directionClass, textAlignClass, isRTL } = useRTLUtils();

  const triggerClass =
    theme === 'enterprise'
      ? 'inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-amber-400/35 text-amber-100 hover:bg-amber-950/45 hover:text-amber-50'
      : 'inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-yellow-200 text-yellow-700 hover:bg-yellow-50 dark:border-yellow-900/50 dark:text-yellow-300 dark:hover:bg-yellow-900/20';

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(triggerClass, className)}
          aria-label={t('language.switchLanguage', 'Switch Language')}
        >
          <GlobeIcon size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={isRTL ? 'start' : 'end'}
        className="z-[210] min-w-[10rem]"
      >
        {supportedLanguages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            className={cn('cursor-pointer', directionClass)}
            onClick={() => {
              void switchAppLanguage(language.code, changeLanguage);
            }}
          >
            <span className={textAlignClass}>{language.name}</span>
            {currentLanguage.code === language.code ? (
              <div
                className={cn(
                  'ms-auto h-2 w-2 rounded-full bg-primary',
                  isRTL && 'ms-0 me-auto',
                )}
              />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
